import maxBy from 'lodash/maxBy';
import isNil from 'lodash/isNil';
import sumBy from 'lodash/sumBy';
import {
  CcmMonthlyPatientRowDTO,
} from 'dtos';
import {
  CcmMonthlyCallRow,
  CcmMonthlyPatientSummaryRow,
  CcmMonthlyRow,
} from 'pages/Dashboard/pages/Dashboard/CcmMonthly/types';

function getPatientRowId({
  patientId,
  reportingMonth,
}: Pick<CcmMonthlyPatientRowDTO, 'patientId' | 'reportingMonth'>): string {
  return `${patientId ?? 'unknown'}-${reportingMonth ?? 'unknown'}`;
}

function normalizeCallRows(
  row: CcmMonthlyPatientRowDTO,
): CcmMonthlyCallRow[] {
  const patientRowId = getPatientRowId(row);
  const calls = row.calls ?? [];

  return calls.map((call, index) => ({
    ...call,
    id: `${patientRowId}-call-${call.callId ?? index}`,
    patientId: row.patientId,
    patientName: row.patientName,
    reportingMonth: row.reportingMonth,
    attemptNumber: calls.length - index,
    isCallDetail: true,
    expandable: false,
  }));
}

export function formatDuration(durationSeconds?: number | null): string | null {
  if (isNil(durationSeconds)) {
    return null;
  }

  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;

  return `${minutes > 0 ? `${minutes}m ` : ''}${seconds}s`;
}

export default function normalizeCcmMonthlyRows(
  rows: CcmMonthlyPatientRowDTO[],
): CcmMonthlyRow[] {
  return rows.map((row): CcmMonthlyPatientSummaryRow => {
    const calls = normalizeCallRows(row);
    const latestCall = maxBy(
      calls,
      ({ calledOn }) => new Date(calledOn ?? '').getTime() || 0,
    );

    return {
      patientId: row.patientId,
      patientName: row.patientName,
      reportingMonth: row.reportingMonth,
      claimStatus: row.claimStatus,
      id: getPatientRowId(row),
      calls,
      callCount: calls.length,
      latestCallOn: latestCall?.calledOn,
      totalDurationSeconds: sumBy(calls, ({ durationSeconds }) => durationSeconds ?? 0),
      isCallDetail: false,
      expandable: calls.length > 0,
    };
  });
}
